import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Select from "react-select";
import { useCreateExternalChargeMutation } from "../../store/api/externalChargeSlice";
import "./ReceivePayment.scss";

const CURRENCIES = [
  { value: "USD", label: "USD — US Dollar" },
  { value: "CAD", label: "CAD — Canadian Dollar" },
];

const ReceivePayment = () => {
  const { t } = useTranslation();

  const [form, setForm] = useState({
    payer_name: "",
    payer_email: "",
    payer_phone: "",
    amount: "",
    currency: { value: "USD", label: "USD — US Dollar" },
    description: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastCharge, setLastCharge] = useState(null);

  const [createCharge] = useCreateExternalChargeMutation();

  const set = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const validate = () => {
    const e = {};
    if (!form.payer_name.trim()) e.payer_name = "Payer name is required.";
    if (!form.payer_email.trim()) e.payer_email = "Payer email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.payer_email))
      e.payer_email = "Enter a valid email address.";
    if (!form.amount || isNaN(form.amount) || Number(form.amount) <= 0)
      e.amount = "Enter a valid amount greater than $0.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const amount_cents = Math.round(Number(form.amount) * 100);
    setLoading(true);

    try {
      const res = await createCharge({
        payer_name: form.payer_name.trim(),
        payer_email: form.payer_email.trim(),
        payer_phone: form.payer_phone.trim() || undefined,
        amount_cents,
        currency: form.currency?.value || "USD",
        description: form.description.trim() || undefined,
      }).unwrap();

      localStorage.setItem("external_charge_id", res.data.external_charge_id);
      localStorage.setItem("external_charge_number", res.data.charge_number);

      setLastCharge({ ...res.data, payer_name: form.payer_name, amount_cents });

      window.open(res.data.iframeUrl, "_blank");
      toast.success(
        "Payment session opened. Share the link or complete in the new tab.",
      );

      // Reset form
      setForm({
        payer_name: "",
        payer_email: "",
        payer_phone: "",
        amount: "",
        currency: { value: "USD", label: "USD — US Dollar" },
        description: "",
      });
    } catch (err) {
      toast.error(err?.data?.message || "Failed to create charge.");
    } finally {
      setLoading(false);
    }
  };

  const currencySymbol = form.currency?.value === "CAD" ? "CA$" : "$";

  return (
    <div className="rp-page">
      {/* Header */}
      <div className="rp-header">
        <div className="rp-header-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
          </svg>
        </div>
        <div>
          <h2 className="rp-title">{t("receivePayment")}</h2>
          <p className="rp-subtitle">{t("chargeParentStudentThirdParty")}</p>
        </div>
      </div>

      <div className="rp-grid">
        {/* Form */}
        <div className="rp-card">
          <form onSubmit={handleSubmit}>
            {/* Step 1 — Payer Info */}
            <div className="rp-section">
              <div className="rp-section-label">
                <span className="rp-step">1</span>
                {t("payer")} {t("information")}
              </div>

              <div className="rp-field">
                <label>
                  {t("full")} {t("name")} <span className="rp-req">*</span>
                </label>
                <input
                  type="text"
                  value={form.payer_name}
                  onChange={(e) => set("payer_name", e.target.value)}
                  placeholder="e.g. John Smith"
                  className={errors.payer_name ? "input-error" : ""}
                />
                {errors.payer_name && (
                  <span className="rp-error">{errors.payer_name}</span>
                )}
              </div>

              <div className="rp-field-row">
                <div className="rp-field rp-field-grow">
                  <label>
                    {t("email")} <span className="rp-req">*</span>
                  </label>
                  <input
                    type="email"
                    value={form.payer_email}
                    onChange={(e) => set("payer_email", e.target.value)}
                    placeholder="email@example.com"
                    className={errors.payer_email ? "input-error" : ""}
                  />
                  {errors.payer_email && (
                    <span className="rp-error">{errors.payer_email}</span>
                  )}
                </div>

                <div className="rp-field rp-field-phone">
                  <label>
                    {t("phone")}{" "}
                    <span className="rp-optional">({t("optional")})</span>
                  </label>
                  <input
                    type="tel"
                    value={form.payer_phone}
                    onChange={(e) => set("payer_phone", e.target.value)}
                    placeholder="+1 (000) 000-0000"
                  />
                </div>
              </div>
            </div>

            {/* Step 2 — Amount */}
            <div className="rp-section">
              <div className="rp-section-label">
                <span className="rp-step">2</span>
                {t("charge")} {t("amount")}
              </div>

              <div className="rp-field-row">
                <div className="rp-field rp-field-grow">
                  <label>
                    {t("amount")} <span className="rp-req">*</span>
                  </label>
                  <div className="rp-amount-input">
                    <span className="rp-currency-symbol">{currencySymbol}</span>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={form.amount}
                      onChange={(e) => set("amount", e.target.value)}
                      placeholder="0.00"
                      className={errors.amount ? "input-error" : ""}
                    />
                  </div>
                  {errors.amount && (
                    <span className="rp-error">{errors.amount}</span>
                  )}
                </div>

                <div className="rp-field rp-field-currency">
                  <label>{t("currency")}</label>
                  <Select
                    options={CURRENCIES}
                    value={form.currency}
                    onChange={(v) => set("currency", v)}
                    classNamePrefix="rp-select"
                  />
                </div>
              </div>
            </div>

            {/* Step 3 — Category & Description */}
            <div className="rp-section">
              <div className="rp-section-label">
                <span className="rp-step">3</span>
                {t("details")}{" "}
                <span className="rp-optional">({t("optional")})</span>
              </div>

              <div className="rp-field">
                <label>
                  {t("description")} / {t("note")}
                </label>
                <textarea
                  rows={3}
                  maxLength={500}
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="e.g. Spring field trip — Grade 5, March 2026"
                />
                <span className="rp-char-count">
                  {form.description.length}/500
                </span>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary rp-submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  />
                  {t("creatingSession")}.....
                </>
              ) : (
                <>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  {t("charge")} {t("now")}
                </>
              )}
            </button>
          </form>
        </div>

        {/* Summary / Info */}
        <div className="rp-card rp-summary-card">
          <h4 className="rp-summary-title">
            {t("charge")} {t("summary")}
          </h4>

          <div className="rp-summary-row">
            <span>{t("payer")}</span>
            <span className="rp-summary-val">
              {form.payer_name || <span className="text-muted">—</span>}
              {form.payer_email && (
                <>
                  <br />
                  <small className="text-muted">{form.payer_email}</small>
                </>
              )}
            </span>
          </div>

          <div className="rp-summary-divider" />

          <div className="rp-summary-row">
            <span>{t("amount")}</span>
            <span className="rp-summary-amount">
              {form.amount ? (
                `${currencySymbol}${Number(form.amount).toFixed(2)} ${form.currency?.value}`
              ) : (
                <span className="text-muted">—</span>
              )}
            </span>
          </div>

          {form.description && (
            <>
              <div className="rp-summary-divider" />
              <div className="rp-summary-row rp-summary-desc">
                <span>{t("note")}</span>
                <span className="rp-summary-val">{form.description}</span>
              </div>
            </>
          )}

          {lastCharge && (
            <div className="rp-last-charge">
              <div className="rp-last-charge-label">
                {t("last")} {t("charge")} {t("created")}
              </div>
              <div className="rp-last-charge-number">
                {lastCharge.charge_number}
              </div>
              <div className="rp-last-charge-payer">
                {lastCharge.payer_name}
              </div>
            </div>
          )}

          <div className="rp-info-box">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p>
              {t("paymentCollected")} <strong>{t("your")}</strong>{" "}
              {t("merchantAccount")}. {t("securePaymentPage")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceivePayment;
